/**
 * Mapper functions for Part (Inventory) entity
 * Direct pass-through — entity matches API response exactly
 */

import { API } from '../api_clients';
import {
  Part,
  PartSummary,
  PartUsageRecord,
} from '../../types/part';

/**
 * Map API ResponsesPart to entity Part
 * Direct pass-through, no field renaming
 */
export function mapApiPartToEntity(
  p: API.ResponsesPart
): Part {
  return {
    unid: p.unid,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    partNumber: p.partNumber || '',
    description: p.description || '',
    category: p.category || 'general',
    vmrsCode: p.vmrsCode || undefined,
    vmrsDescription: p.vmrsDescription || undefined,
    status: p.status || 'active',
    unitCost: p.unitCost ?? 0,
    quantityOnHand: p.quantityOnHand ?? 0,
    minimumStock: p.minimumStock ?? 0,
    maximumStock: p.maximumStock ?? undefined,
    reorderQuantity: p.reorderQuantity ?? undefined,
    binLocation: p.binLocation || undefined,
    preferredSupplier: p.preferredSupplier || undefined,
    warrantyMiles: p.warrantyMiles ?? undefined,
    warrantyMonths: p.warrantyMonths ?? undefined,
    notes: p.notes || undefined,
    isLowStock: p.isLowStock || false,
  };
}

/**
 * PartListItem = Part, same mapper
 */
export const mapApiPartToListItem = mapApiPartToEntity;

/**
 * Map API part to minimal PartSummary
 */
export function mapApiPartToSummary(
  p: API.ResponsesPart
): PartSummary {
  return {
    id: p.unid,
    partNumber: p.partNumber || '',
    description: p.description || '',
    category: p.category || 'general',
    quantityOnHand: p.quantityOnHand ?? 0,
    unitCost: p.unitCost ?? 0,
    isLowStock: p.isLowStock || false,
  };
}

/**
 * Map API PartUsageRecord to entity PartUsageRecord
 */
export function mapApiPartUsageRecordToEntity(
  u: API.ResponsesPartUsageRecord
): PartUsageRecord {
  return {
    unid: u.unid || '',
    quantity: u.quantity ?? 0,
    unitCost: u.unitCost ?? 0,
    totalCost: u.totalCost ?? 0,
    usedAt: u.usedAt || '',
    workOrderId: u.workOrderId || undefined,
    workOrderNumber: u.workOrderNumber || undefined,
    maintenanceId: u.maintenanceId || undefined,
    truckId: u.truckId || undefined,
    truckUnitNumber: u.truckUnitNumber || undefined,
    trailerId: u.trailerId || undefined,
    trailerUnitNumber: u.trailerUnitNumber || undefined,
    notes: u.notes || undefined,
  };
}

/**
 * Reverse mapper: Entity Part to API CreatePartRequest
 */
export function mapEntityPartToCreateRequest(
  part: Partial<Part>
): API.CreatePartRequest {
  return {
    partNumber: part.partNumber || '',
    description: part.description || '',
    category: part.category || 'general',
    vmrsCode: part.vmrsCode || undefined,
    unitCost: part.unitCost ?? undefined,
    quantityOnHand: part.quantityOnHand ?? undefined,
    minimumStock: part.minimumStock ?? undefined,
    maximumStock: part.maximumStock ?? undefined,
    reorderQuantity: part.reorderQuantity ?? undefined,
    binLocation: part.binLocation || undefined,
    preferredSupplier: part.preferredSupplier || undefined,
    warrantyMiles: part.warrantyMiles ?? undefined,
    warrantyMonths: part.warrantyMonths ?? undefined,
    notes: part.notes || undefined,
  };
}

/**
 * Reverse mapper: Entity Part to API UpdatePartRequest
 */
export function mapEntityPartToUpdateRequest(
  part: Partial<Part>
): API.UpdatePartRequest {
  return {
    partNumber: part.partNumber || undefined,
    description: part.description || undefined,
    category: part.category || undefined,
    vmrsCode: part.vmrsCode || undefined,
    unitCost: part.unitCost ?? undefined,
    quantityOnHand: part.quantityOnHand ?? undefined,
    minimumStock: part.minimumStock ?? undefined,
    maximumStock: part.maximumStock ?? undefined,
    reorderQuantity: part.reorderQuantity ?? undefined,
    binLocation: part.binLocation || undefined,
    preferredSupplier: part.preferredSupplier || undefined,
    warrantyMiles: part.warrantyMiles ?? undefined,
    warrantyMonths: part.warrantyMonths ?? undefined,
    notes: part.notes || undefined,
    status: part.status || undefined,
  };
}
